/**
 * AgreementDocumentsTab — the agreement's document set as a SELECT-then-ACT grid
 * (2026-07-05 reorg; spec: _MEMORY/decisions/2026-07-05_documents-tab-reorg-and-portfolio-report.md).
 *
 * Columns: Document · On file - signed · Fillable · Filled-PT · Filled-EN.
 * Every cell is a checkbox; disabled where there is no path today (retired to
 * dashes later). The user ticks artefacts and presses ONE action button.
 *
 * Actions (disabled until >=1 ticked):
 *   - Download individual — per-file sequential download.
 *   - Download zip        — one server-side ZIP of everything selected (D-2).
 *   (Email lives in the page header since P34 — Pedro 2026-10-14, option A: one way in. Its
 *   dialog holds this grid as variant 'email', with the same questions.)
 *
 * The IAA needs Parties/Date, collected by a modal that fires FIRST when an IAA
 * cell is ticked. The modal is a generic parameter-collector: on confirm it
 * calls onSubmit(params) — for Download individual that generates the IAA
 * draft(s) then the other files; for Download zip it POSTs the whole selection
 * (IAA inside the ZIP). Either onSubmit may throw IaaExportBlockedError, which
 * the modal renders in-place as a per-person missing-field list (C-134/§3j).
 * Selection clears once the action completes.
 *
 * "On file - signed" (P23, Pedro 2026-10-10) shows the signed copy on file for each
 * document — the most recent of its type — with View / Download, plus its pending
 * state (orange "Pending Save" when staged, blue "Pending · Upload" when filed for
 * approval). In Edit mode it offers Add / Replace, which STAGES an upload of that
 * type (sent on the page's Save; a replacement keeps the earlier copy in the list
 * below). The bank-transfer template has no signed copy. The full list of files on
 * this agreement — every type, every version, removed ones on request — is the
 * "Documents on file" card under the grid (the shared DocumentsPanel).
 * "Portfolio report" (row #1) is the per-client MIS document — scaffolded disabled;
 * its generator lands with the MIS module.
 *
 * Generation is Pattern B (the action IS the commit gesture of a generation
 * act; nothing on the entity mutates).
 *
 * Fee simulation (Pedro 2026-10-10, as on the Blank legal templates page): with an Annex I
 * PDF ticked, either Download first asks "Include a fee simulation?". Yes opens one worked
 * example per fee schedule assigned to this agreement's accounts (the annex prints every
 * schedule's terms), each billed as those accounts are: their billing frequencies, all of
 * them, and their VAT (Pedro 2026-10-11, replacing the Monthly default of the same morning;
 * no frequency set on the accounts → every frequency shown). Each amount starts from the
 * accounts' latest fee-basis AuM (blank when there is none, e.g. a Prospect). Continue runs
 * the normal download with the examples in the Annex I PDF(s); the Word Annex I stays
 * without them. This runs before the IAA dialog when both are ticked.
 *
 * Client record (P16b step c, Pedro 2026-10-10): the Filled-PT / Filled-EN cells generate
 * each picked party's record, pre-filled from their data, as a PDF for them to check and
 * sign (one file per person per language). Either Download first asks whose records to
 * generate (the parties of this agreement, holders ticked) — after the fee-simulation
 * question, before the IAA dialog. The blank form is on the Blank legal templates page.
 */
import {
  ActionIcon, Alert, Anchor, Badge, Button, Card, Checkbox, Collapse, Group, Modal,
  NumberInput, Select, SimpleGrid, Stack, Table, Text, TextInput, Title, Tooltip, UnstyledButton,
} from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle, IconCalculator, IconCheck, IconChevronDown, IconChevronRight, IconDownload,
  IconFiles, IconFileText, IconFileZip, IconUpload,
} from '@tabler/icons-react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import type { ApiError } from '../api/client';
import { feeSchedulesApi, type FeeExampleOut, type FeeFrequency } from '../api/feeSchedules';
import {
  IaaExportBlockedError, type IaaExportMissingRecord,
  downloadBlob, exportIaaDraft,
} from '../api/iaaExports';
import {
  accountBillingApi, type AccountFeePreview, type BillingIdentityRow,
} from '../api/accountBilling';
import { AqNoAssessmentError, exportAqDraft } from '../api/aqExports';
import { downloadAqFillable } from '../api/aqImports';
import { downloadClientRecord } from '../api/clientRecord';
import { downloadLegalTemplate, type LegalTemplateKind } from '../api/legalTemplates';
import {
  downloadAnnex, type AnnexFeeExample, type AnnexFormat, type AnnexKind,
} from '../api/staticAnnexes';
import {
  downloadDocumentsZip, type DocumentsZipItem, type DocumentsZipRequestBody,
} from '../api/documentsZip';
import type { GeneratedSelection } from './EmailDraftModal';
import {
  PARTY_ROLE_LABELS, type Agreement, type AgreementParty, type DocumentLanguage, type UUID,
} from '../api/types';
import { useAgreementDocumentsByAgreement } from '../hooks/useAgreementDocuments';
import { useAgreementParties } from '../hooks/useAgreementParties';
import {
  kindsFor, useDocumentKinds, useRecordDocuments, type DocumentStaging,
} from '../hooks/useDocuments';
import { usePermissions } from '../hooks/usePermissions';
import { AgreementReportsSection } from './AgreementReportsSection';
import { useAlertModal } from './AlertModalProvider';
import {
  AddDocumentModal, DocumentFileActions, DocumentsPanel, PendingUploadBadge,
} from './DocumentsPanel';
import { FeeExampleView, FREQ_LABEL } from './FeeCalculatorCard';

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

// ─── Column / row model ──────────────────────────────────────────────────────

type Col = 'onfile' | 'fillable' | 'pt' | 'en';
/** The three actions on a selection: files one by one, one ZIP, or a draft email (P27). */
type DownloadMode = 'individual' | 'zip' | 'email';
type RowKind = 'banktransfer' | 'client' | 'amlkyc' | 'iaa' | 'fees' | 'aq' | 'pp' | 'samplemonthly';

interface IaaParams {
  parties: { party_id: string; position?: string }[];
  document_date: string;
}

const COLS: { key: Col; label: string }[] = [
  { key: 'onfile', label: 'On file - signed' },
  { key: 'fillable', label: 'Fillable' },
  { key: 'pt', label: 'Filled-PT' },
  { key: 'en', label: 'Filled-EN' },
];

const DOC_SHORT: Record<RowKind, string> = {
  banktransfer: 'Bank transfer',
  client: 'Client Record',
  amlkyc: 'AML/KYC Record',
  iaa: 'IAA',
  fees: 'Annex I',
  aq: 'Annex II',
  pp: 'Annex III',
  samplemonthly: 'Annex IV',
};

// P23: the signed-copy document type behind each row's "On file - signed" cell.
const SIGNED_KIND: Partial<Record<RowKind, string>> = {
  client: 'CLIENT_RECORD',
  amlkyc: 'AML_KYC_RECORD',
  iaa: 'IAA',
  fees: 'ANNEX_I',
  aq: 'ANNEX_II',
  pp: 'ANNEX_III',
  samplemonthly: 'ANNEX_IV',
};

const langOf = (col: Col): DocumentLanguage => (col === 'pt' ? 'PT' : 'EN');

// The rows' names (also used, with language and format, to name an emailed document).
const ROW_NAME: Record<RowKind, string> = {
  banktransfer: 'Bank transfer template',
  client: 'Client Record',
  amlkyc: 'AML/KYC Record + score',
  iaa: 'Investment Advisory Agreement (IAA)',
  fees: 'Annex I — Fees',
  aq: 'Annex II — Adequacy questionnaire',
  pp: 'Annex III — Policies & procedures',
  samplemonthly: 'Annex IV — Sample monthly report',
};

/** P34: a ticked cell named as on the Blank legal templates rows, e.g.
 *  "Annex III — Policies & procedures (PT, Word)" or "Annex II — Adequacy questionnaire (Fillable)". */
function emailLabel(it: { document: string; column: string; format?: string }): string {
  const name = ROW_NAME[it.document as RowKind] ?? it.document;
  if (it.column === 'fillable') return `${name} (Fillable)`;
  const fmt = it.format ? `, ${it.format === 'pdf' ? 'PDF' : 'Word'}` : '';
  return `${name} (${it.column.toUpperCase()}${fmt})`;
}

/** DF1 (Pedro 2026-10-12): the blank client-record forms an agreement needs — the
 *  individual form; plus the legal-person form when any party is a legal person. Same rule
 *  as the server's `client_record_pdf.blank_form_kinds` (Download zip / Email). */
function blankClientFormKinds(parties: AgreementParty[]): LegalTemplateKind[] {
  const hasLegal = parties.some((p) => !p.deleted_at && p.person_kind === 'LEGAL');
  return hasLegal ? ['clientform_individual', 'clientform_legal'] : ['clientform_individual'];
}
const itemLabel = (kind: RowKind, col: Col, fmt?: AnnexFormat): string =>
  `${DOC_SHORT[kind]} (${col === 'fillable' ? 'Fillable' : col.toUpperCase()}`
  + `${fmt ? ` ${fmt.toUpperCase()}` : ''})`;

/** Whether a (row, column) cell is actionable today, and if not, why. */
function cellState(kind: RowKind, col: Col): { enabled: boolean; reason?: string } {
  if (col === 'onfile') {
    // Rendered by OnFileCell (status + View / Add), not as a selection checkbox.
    return SIGNED_KIND[kind]
      ? { enabled: true }
      : { enabled: false, reason: 'This template has no signed copy.' };
  }
  if (kind === 'banktransfer') {
    return (col === 'pt' || col === 'en')
      ? { enabled: false, reason: 'Coming soon — no generator for this template yet.' }
      : { enabled: false, reason: 'Not applicable to this document.' };
  }
  if (kind === 'client') {
    // P16b step (c): the app generates each party's pre-filled record. DF1 (Pedro
    // 2026-10-12): Fillable = the blank form(s) — individual; plus legal-person when a
    // legal person is a party (see blankClientFormKinds).
    return { enabled: true };
  }
  if (kind === 'amlkyc') {
    return { enabled: false, reason: 'No generator for this document yet.' };
  }
  if (col === 'fillable') {
    return kind === 'aq'
      ? { enabled: true }
      : { enabled: false, reason: 'No fillable form for this document.' };
  }
  // Filled-PT / Filled-EN. samplemonthly = the static Annex IV sample (PT/EN), served
  // verbatim like Annex III P&P.
  if (
    kind === 'iaa' || kind === 'fees' || kind === 'aq' || kind === 'pp'
    || kind === 'samplemonthly'
  ) return { enabled: true };
  return { enabled: false, reason: 'Not available.' };
}

// Output formats a Filled-PT/EN cell offers, and whether each is available TODAY
// (Legal-docs PDF arc — the per-cell [Word | PDF] model). Only Annex I (fees) has
// PDF this increment; iaa/aq/pp are Word-until-their-increment; the samplemonthly
// exhibit is PDF-only. Combined with cellState (which gates the cell at all).
const _LATER = 'A PDF of this document arrives in a later increment.';
function filledFormats(kind: RowKind): { fmt: AnnexFormat; enabled: boolean; reason?: string }[] {
  switch (kind) {
    case 'fees':   // data-populated on-brand PDF (Inc 1)
    case 'pp':     // static on-brand PDF exported from Word (Inc 2)
      return [{ fmt: 'word', enabled: true }, { fmt: 'pdf', enabled: true }];
    case 'iaa':
    case 'aq':
      return [{ fmt: 'word', enabled: true }, { fmt: 'pdf', enabled: false, reason: _LATER }];
    case 'samplemonthly':
      return [
        { fmt: 'word', enabled: false, reason: 'This sample report is provided as a PDF.' },
        { fmt: 'pdf', enabled: true },
      ];
    case 'client':
      return [
        { fmt: 'word', enabled: false, reason: 'The client record is provided as a PDF.' },
        { fmt: 'pdf', enabled: true },
      ];
    default:
      return [{ fmt: 'word', enabled: false }, { fmt: 'pdf', enabled: false }];
  }
}

// ─── IAA Parties/Date modal — a generic parameter collector ──────────────────

function ExportIaaModal({
  agreement, holders, langs, opened, onClose, onSubmit, submitLabel,
}: {
  agreement: Agreement;
  holders: AgreementParty[];
  langs: DocumentLanguage[];
  opened: boolean;
  onClose: () => void;
  onSubmit: (params: IaaParams) => Promise<void>;
  submitLabel: string;
}) {
  const [selected, setSelected] = useState<string[]>(
    holders.length === 1 ? [holders[0].id] : [],
  );
  const [positions, setPositions] = useState<Record<string, string>>({});
  const [docDate, setDocDate] = useState<string>(todayISO());
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState<IaaExportMissingRecord[] | null>(null);
  const { showAlert } = useAlertModal();

  const selectedLegal = holders.filter(
    (h) => selected.includes(h.id) && h.person_kind === 'LEGAL',
  );
  const positionsMissing = selectedLegal.some((h) => !(positions[h.id] ?? '').trim());
  const invalid = selected.length === 0 || !docDate || positionsMissing;

  async function handleSubmit() {
    if (invalid) return; // defensive; button disabled in this state
    setBusy(true);
    setBlocked(null);
    try {
      await onSubmit({
        parties: selected.map((id) => ({
          party_id: id,
          ...(positions[id]?.trim() ? { position: positions[id].trim() } : {}),
        })),
        document_date: docDate,
      });
      onClose(); // parent already did the work (downloads + toast + clear)
    } catch (e) {
      if (e instanceof IaaExportBlockedError) {
        setBlocked(e.missing); // rendered in-modal with links (§3j)
      } else {
        showAlert({
          color: 'red',
          title: 'Export failed',
          message: e instanceof Error ? e.message : 'Unknown error',
        });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      opened={opened} onClose={onClose} centered size="lg"
      title={(
        <Group gap="xs">
          <IconFileText size={18} />
          <Text fw={600}>Export IAA draft ({langs.join(' + ')}) — {agreement.code_1}</Text>
        </Group>
      )}
    >
      <Stack>
        <Text size="sm" c="dimmed">
          Pick the Holder(s) the draft is addressed to. The export is always a
          watermarked <strong>draft</strong> — the authoritative IAA is the signed
          copy on file. Language comes from the Filled-PT / Filled-EN boxes you
          ticked ({langs.join(' + ')}). Every export is recorded in the audit log.
        </Text>

        <Checkbox.Group label="Holders (parties to the IAA)" value={selected} onChange={setSelected}>
          <Stack gap="xs" mt="xs">
            {holders.map((h) => (
              <Checkbox
                key={h.id}
                value={h.id}
                label={(
                  <Group gap="xs">
                    <Text size="sm">{h.person_display_name ?? '(unnamed person)'}</Text>
                    {h.person_kind === 'LEGAL' && <Badge size="xs" variant="light">Empresa</Badge>}
                  </Group>
                )}
              />
            ))}
          </Stack>
        </Checkbox.Group>

        {selectedLegal.map((h) => (
          <TextInput
            key={h.id}
            label={`Position — representative of ${h.person_display_name ?? 'the company'}`}
            description="The capacity in which the representative signs (e.g. Administrador, Gerente)."
            value={positions[h.id] ?? ''}
            onChange={(e) => setPositions({ ...positions, [h.id]: e.currentTarget.value })}
            required
          />
        ))}

        <TextInput
          label="Document date" type="date" value={docDate}
          onChange={(e) => setDocDate(e.currentTarget.value)} required
          style={{ maxWidth: 220 }}
        />
        <Text size="xs" c="dimmed">Place of signing: Lisboa (fixed).</Text>

        {blocked && (
          <Alert color="orange" icon={<IconAlertCircle size={16} />}
            title="Required data is missing — complete the records below, then export again.">
            <Stack gap="xs">
              {blocked.map((m, i) => (
                <div key={i}>
                  {m.person_id ? (
                    <Anchor component={Link} to={`/persons/${m.person_id}`} size="sm" fw={600}>
                      {m.person_name}
                    </Anchor>
                  ) : (
                    <Text size="sm" fw={600} span>{m.person_name}</Text>
                  )}
                  <Text size="xs" c="dimmed">{m.fields.join(' · ')}</Text>
                </div>
              ))}
            </Stack>
          </Alert>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit} loading={busy} disabled={invalid}
            leftSection={<IconDownload size={16} />}
          >
            {submitLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ─── Fee simulation: one worked example per fee schedule (Pedro 2026-10-11) ─

const FREQ_ORDER: FeeFrequency[] = ['monthly', 'quarterly', 'semiannual'];

/** One fee schedule on this agreement's accounts, as the Annex I PDF prints it. */
interface ScheduleSimulation {
  scheduleId: UUID;
  name: string;
  accounts: number;
  /** The billing frequencies its accounts use, in calendar order; empty = none set (the
   *  example then shows every frequency). */
  frequencies: FeeFrequency[];
  /** The frequency the example starts on (Pedro's smoke 2026-10-11: chosen next to the
   *  amount): the one most of its accounts use (a tie → the more frequent billing); Monthly,
   *  the calculator's default, when none is set. */
  defaultFrequency: FeeFrequency;
  vatRateBps: number | null;
  /** The accounts' latest fee-basis AuM in euros; null when there is none (a Prospect). */
  defaultAmount: number | null;
}

/** The schedules in the annex's own order, with each one's frequencies and VAT taken from
 *  its accounts exactly as the server does (`fee_annex_pdf.agreement_fee_examples`): rows
 *  sorted direct-first, then bank / insurer, then account / policy number; VAT = the first
 *  account's that has one. */
function scheduleSimulations(
  billing: BillingIdentityRow[], preview: AccountFeePreview[],
): ScheduleSimulation[] {
  const key = (r: BillingIdentityRow): [number, string, string] => [
    r.is_direct ? 0 : 1, r.bank_name || r.insurer || '', r.account_number || r.policy_number || ''];
  const cmp = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);
  const rows = billing.filter((r) => r.fee_schedule_id).sort((a, b) => {
    const [ka, kb] = [key(a), key(b)];
    return ka[0] - kb[0] || cmp(ka[1], kb[1]) || cmp(ka[2], kb[2]);
  });
  const aumOf = new Map(preview.map((p) => [p.account_id, p.aum_basis_cents]));
  type Acc = ScheduleSimulation & { aumCents: number[]; freqCount: Map<FeeFrequency, number> };
  const out = new Map<UUID, Acc>();
  for (const r of rows) {
    const sid = r.fee_schedule_id as UUID;
    let s = out.get(sid);
    if (!s) {
      s = { scheduleId: sid, name: r.fee_schedule_name ?? 'Fee schedule', accounts: 0,
        frequencies: [], defaultFrequency: 'monthly', vatRateBps: null, defaultAmount: null,
        aumCents: [], freqCount: new Map() };
      out.set(sid, s);
    }
    s.accounts += 1;
    const f = r.fee_frequency as FeeFrequency | null;
    if (f && FREQ_ORDER.includes(f)) {
      if (!s.frequencies.includes(f)) s.frequencies.push(f);
      s.freqCount.set(f, (s.freqCount.get(f) ?? 0) + 1);
    }
    if (s.vatRateBps === null && r.vat_rate_bps != null) s.vatRateBps = r.vat_rate_bps;
    const aum = aumOf.get(r.account_id);
    if (aum != null) s.aumCents.push(aum);
  }
  return [...out.values()].map(({ aumCents, freqCount, ...s }) => ({
    ...s,
    frequencies: [...s.frequencies].sort((a, b) => FREQ_ORDER.indexOf(a) - FREQ_ORDER.indexOf(b)),
    // Most accounts' frequency; FREQ_ORDER breaks a tie (the first, i.e. more frequent, wins).
    defaultFrequency: FREQ_ORDER.reduce<FeeFrequency>(
      (best, f) => ((freqCount.get(f) ?? 0) > (freqCount.get(best) ?? 0) ? f : best), FREQ_ORDER[0]),
    defaultAmount: aumCents.length ? aumCents.reduce((a, b) => a + b, 0) / 100 : null,
  }));
}

function billingSummary(s: ScheduleSimulation): string {
  const accounts = `${s.accounts} account${s.accounts === 1 ? '' : 's'}`;
  if (!s.frequencies.length) {
    return `${accounts} · no billing frequency set, so every frequency is shown`;
  }
  const names = s.frequencies.map((f) => FREQ_LABEL[f].toLowerCase());
  const list = names.length === 1
    ? names[0]
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `${accounts} · billed ${list}`;
}

function ScheduleSimulationCard({ sim, amount, onAmount, frequency, onFrequency, example, error }: {
  sim: ScheduleSimulation;
  amount: number | null;
  onAmount: (v: number | null) => void;
  frequency: FeeFrequency;
  onFrequency: (f: FeeFrequency) => void;
  example: FeeExampleOut | undefined;
  error: string | null;
}) {
  return (
    <Card withBorder padding="md">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-end" wrap="nowrap">
          <Stack gap={2}>
            <Text fw={600}>{sim.name}</Text>
            <Text size="xs" c="dimmed">{billingSummary(sim)}</Text>
          </Stack>
          <Group gap="sm" wrap="nowrap" align="flex-end">
            <NumberInput label="Amount (€)" w={200} value={amount ?? ''} min={0} step={100_000}
              thousandSeparator="," decimalScale={2} placeholder="Type the amount"
              onChange={(v) => onAmount(typeof v === 'number' ? v : null)} />
            {/* Same control as the fee calculator's (FeeCalculatorCard). */}
            <Select label="Billing frequency" w={160} allowDeselect={false} value={frequency}
              data={FREQ_ORDER.map((f) => ({ value: f, label: FREQ_LABEL[f] }))}
              onChange={(v) => v && onFrequency(v as FeeFrequency)}
              comboboxProps={{ withinPortal: true }} />
          </Group>
        </Group>
        {error && <Alert color="red" title="Could not compute the example">{error}</Alert>}
        {amount !== null && example && <FeeExampleView ex={example} />}
      </Stack>
    </Card>
  );
}

// ─── Client record: whose record(s) to generate (P16b step c) ───────────────

interface ClientPick { personId: UUID; name: string }

/** The agreement's parties, one line per person (a person with two roles appears once,
 *  with both), holders ticked. Continue hands the picks back; the download runs after. */
function ClientRecordPartiesModal({
  agreement, parties, opened, onClose, onContinue,
}: {
  agreement: Agreement;
  parties: AgreementParty[];
  opened: boolean;
  onClose: () => void;
  onContinue: (picks: ClientPick[]) => void;
}) {
  const people = useMemo(() => {
    const byPerson = new Map<UUID, { name: string; roles: string[]; holder: boolean }>();
    for (const p of parties) {
      if (p.deleted_at) continue;
      const entry = byPerson.get(p.person_id)
        ?? { name: p.person_display_name ?? '(unnamed person)', roles: [], holder: false };
      const role = PARTY_ROLE_LABELS[p.role] ?? p.role;
      if (!entry.roles.includes(role)) entry.roles.push(role);
      if (p.role === 'HOLDER') entry.holder = true;
      byPerson.set(p.person_id, entry);
    }
    return [...byPerson.entries()].map(([id, e]) => ({ id, ...e }));
  }, [parties]);
  // Mounted only while open, so the holder default is taken when the dialog opens.
  const [selected, setSelected] = useState<string[]>(
    () => people.filter((p) => p.holder).map((p) => p.id),
  );

  return (
    <Modal
      opened={opened} onClose={onClose} centered size="lg"
      title={(
        <Group gap="xs">
          <IconFileText size={18} />
          <Text fw={600}>Client record — {agreement.code_1}</Text>
        </Group>
      )}
    >
      <Stack>
        <Text size="sm" c="dimmed">
          Pick whose client record to generate. Each record is filled in from that person's
          data, for them to check and sign; the signed copy then goes on file under
          "On file - signed". You get one PDF per person for each language you ticked.
        </Text>
        {people.length === 0 ? (
          <Alert color="orange" icon={<IconAlertCircle size={16} />}>
            This agreement has no parties yet, so there is no client record to generate.
          </Alert>
        ) : (
          <Checkbox.Group label="Parties of this agreement" value={selected} onChange={setSelected}>
            <Stack gap="xs" mt="xs">
              {people.map((p) => (
                <Checkbox
                  key={p.id}
                  value={p.id}
                  label={(
                    <Group gap="xs">
                      <Text size="sm">{p.name}</Text>
                      <Text size="xs" c="dimmed">{p.roles.join(' · ')}</Text>
                    </Group>
                  )}
                />
              ))}
            </Stack>
          </Checkbox.Group>
        )}
        <Text size="xs" c="dimmed">Place of signing: Lisboa (fixed).</Text>
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button
            disabled={selected.length === 0}
            onClick={() => onContinue(people
              .filter((p) => selected.includes(p.id))
              .map((p) => ({ personId: p.id, name: p.name })))}
          >
            Continue
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ─── "On file - signed" cell (P23) ─────────────────────────────────────────

/** The signed copy on file for one legal document: View / Download the latest of its
 *  type, its pending state, and (Edit mode) Add / Replace, which stages an upload. */
function OnFileCell({
  docKind, files, staging, editMode, canWrite, onAdd,
}: {
  docKind: string;
  files: ReturnType<typeof useRecordDocuments>['data'];
  staging: DocumentStaging;
  editMode: boolean;
  canWrite: boolean;
  onAdd: (docKind: string) => void;
}) {
  const current = (files?.documents ?? []).find((d) => d.kind === docKind && !d.deleted_at);
  const filed = (files?.pending_uploads ?? []).find((p) => p.kind === docKind);
  const staged = staging.queue.ops.some((op) => op.kind === 'upload' && op.docKind === docKind);
  const removing = !!current && staging.queue.ops.some(
    (op) => op.kind === 'remove' && op.document_id === current.id);
  const addLabel = current ? 'Replace the signed copy (uploads on Save; the earlier copy stays listed below)'
    : 'Add the signed copy (uploads on Save)';
  return (
    <Stack gap={2} align="center">
      <Group justify="center" gap={2} wrap="nowrap">
        {current ? <DocumentFileActions doc={current} />
          : !staged && !filed && <Text size="xs" c="dimmed">—</Text>}
        {canWrite && (editMode ? (
          <Tooltip label={addLabel} withArrow multiline w={240}>
            <ActionIcon variant="subtle" color="orange" aria-label={addLabel} onClick={() => onAdd(docKind)}>
              <IconUpload size={16} />
            </ActionIcon>
          </Tooltip>
        ) : (
          <Tooltip label="Enter Edit mode to add or replace the signed copy" withArrow>
            <span style={{ display: 'inline-flex' }}>
              <ActionIcon variant="subtle" disabled aria-label="Add the signed copy"><IconUpload size={16} /></ActionIcon>
            </span>
          </Tooltip>
        ))}
      </Group>
      {current?.created_at && (
        <Text size="10px" c="dimmed">{new Date(current.created_at).toLocaleDateString()}</Text>
      )}
      {removing && <Badge color="orange" variant="light" size="xs">Pending remove</Badge>}
      {staged && <Badge color="orange" variant="light" size="xs">Pending Save</Badge>}
      {filed && <PendingUploadBadge p={filed} />}
    </Stack>
  );
}

// ─── The Documents tab ─────────────────────────────────────────────────────

export function AgreementDocumentsTab({
  agreement, editMode = false, staging, variant = 'tab', onEmailSelection,
  onPendingChange, onTickedLabels, attachSignal = 0, onAttachCancelled,
}: {
  agreement: Agreement;
  /** The page's Edit mode: uploads stage into its transaction (P23, §3g Pattern A). */
  editMode?: boolean;
  /** Required for the Docs tab (the On file - signed uploads); unused in the email variant. */
  staging?: DocumentStaging;
  /** P34 (Pedro 2026-10-14, smoke line 7): 'email' = the Legal documents grid only, inside
   *  the Email dialog, with the same pickers (fee simulation, client records, IAA); the
   *  dialog's Prepare email runs them (attachSignal) and gets the generated selection. */
  variant?: 'tab' | 'email';
  /** Email variant: the attached selection, or null when the ticks changed since. */
  onEmailSelection?: (g: GeneratedSelection | null) => void;
  /** Email variant: true while cells are ticked but not yet generated-and-attached. */
  onPendingChange?: (pending: boolean) => void;
  /** Email variant: the ticked cells' names, as they change (for the standard text). */
  onTickedLabels?: (labels: string[]) => void;
  /** Email variant: each increase runs the questions and attaches (Pedro's smoke 2026-10-14:
   *  one step — the dialog's Prepare email does it). */
  attachSignal?: number;
  /** Email variant: a question was closed without going on (the dialog stops waiting). */
  onAttachCancelled?: () => void;
}) {
  const asEmail = variant === 'email';
  const partiesQ = useAgreementParties({ agreement_id: agreement.id, limit: 200 });
  const docsQ = useAgreementDocumentsByAgreement(agreement.id);
  // P23: files on this agreement (signed legal documents + other documents).
  const filesQ = useRecordDocuments('agreements', agreement.id);
  const kindsQ = useDocumentKinds();
  const { can } = usePermissions();
  const { showAlert } = useAlertModal();
  const canWriteFiles = can('record.write');
  const [addKind, setAddKind] = useState<string | null>(null);
  const [modalOpened, { open: openModal, close: closeModalRaw }] = useDisclosure(false);
  const [legalOpen, { toggle: toggleLegal }] = useDisclosure(false); // Legal docs collapsed on start
  const [modalLangs, setModalLangs] = useState<DocumentLanguage[]>([]);
  const [modalMode, setModalMode] = useState<DownloadMode>('individual');
  const [pendingOthers, setPendingOthers] = useState<{ kind: RowKind; col: Col; fmt?: AnnexFormat }[]>([]);
  const [indivBusy, setIndivBusy] = useState(false);
  const [zipBusy, setZipBusy] = useState(false);
  // P34: email is the page header's Email (Pedro 2026-10-14, option A: one way in); its dialog
  // holds this grid as variant 'email'.
  // Email variant: the selection last attached, to tell ticks apart from it.
  const [attachedKey, setAttachedKey] = useState<string | null>(null);

  const holders = (partiesQ.data?.items ?? []).filter(
    (p) => p.role === 'HOLDER' && !p.deleted_at,
  );

  // ── Fee simulation in Annex I (Pedro 2026-10-10) ──
  // Same query keys as BillingIdentitySection, so the Fees tab and this tab share the cache.
  const billingQ = useQuery({
    queryKey: ['account-billing', agreement.id],
    queryFn: () => accountBillingApi.list(agreement.id),
    enabled: legalOpen || asEmail,
  });
  const previewQ = useQuery({
    queryKey: ['account-fee-preview', agreement.id],
    queryFn: () => accountBillingApi.feePreview(agreement.id),
    enabled: legalOpen || asEmail,
  });
  const scheduleSims = useMemo(
    () => scheduleSimulations(billingQ.data ?? [], previewQ.data ?? []),
    [billingQ.data, previewQ.data],
  );
  const [askSimulation, setAskSimulation] = useState<DownloadMode | null>(null);
  const [calcFor, setCalcFor] = useState<DownloadMode | null>(null);
  // The amount typed for each schedule (null = none yet, e.g. a Prospect with no AuM).
  const [simAmounts, setSimAmounts] = useState<Record<string, number | null>>({});
  // The billing frequency chosen next to each amount (Pedro's smoke 2026-10-11).
  const [simFreqs, setSimFreqs] = useState<Record<string, FeeFrequency>>({});
  const freqOf = (s: ScheduleSimulation): FeeFrequency => simFreqs[s.scheduleId] ?? s.defaultFrequency;
  // A ref, not state: the IAA dialog's later submit must see the choice made here.
  const simulationRef = useRef<AnnexFeeExample[] | null>(null);
  // Client record (P16b step c): whose records the current download generates; a ref for
  // the same reason. Null = not asked yet for this download.
  const clientPicksRef = useRef<ClientPick[] | null>(null);
  const [askClientFor, setAskClientFor] = useState<DownloadMode | null>(null);

  // Each schedule's worked example, recomputed once typing pauses (the calculator idiom).
  const [debouncedAmounts] = useDebouncedValue(simAmounts, 300);
  const exampleQs = useQueries({
    queries: scheduleSims.map((s) => {
      const amount = debouncedAmounts[s.scheduleId] ?? null;
      return {
        queryKey: ['fee-schedules', 'example', s.scheduleId, amount, freqOf(s), s.vatRateBps],
        queryFn: () => feeSchedulesApi.example(s.scheduleId, amount as number, freqOf(s),
          s.vatRateBps),
        enabled: calcFor !== null && amount !== null && amount >= 0,
        // keepPreviousData, typed (the generic form loses the data type inside useQueries).
        placeholderData: (prev: FeeExampleOut | undefined) => prev,
      };
    }),
  });
  // Continue only once every schedule's example matches the amount on screen.
  const simulationReady = scheduleSims.length > 0 && scheduleSims.every((s, i) => {
    const amount = simAmounts[s.scheduleId] ?? null;
    const q = exampleQs[i];
    return amount !== null && amount === debouncedAmounts[s.scheduleId] && !!q?.data
      && !q.error && !q.isPlaceholderData;
  });

  // Selection: a Set of keys. Filled-PT/EN cells carry a format axis
  // (`${kind}:${col}:${fmt}`); the format-less cells (fillable) stay `${kind}:${col}`.
  const [sel, setSel] = useState<Set<string>>(new Set());
  const selKey = [...sel].sort().join('|');
  // Email variant: ticks that differ from what was attached detach it until re-attached.
  useEffect(() => {
    if (asEmail && selKey !== attachedKey) onEmailSelection?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asEmail, selKey, attachedKey]);
  // Set the moment an attach succeeds (a ref: the closing question must see it at once).
  const attachedRef = useRef(false);
  useEffect(() => { attachedRef.current = false; }, [selKey]);
  /** A question closed: tell the email dialog, unless the documents were just attached. */
  function questionClosed() {
    if (asEmail && !attachedRef.current) onAttachCancelled?.();
  }
  useEffect(() => {
    if (!asEmail) return;
    onTickedLabels?.([...sel].sort().map((k) => {
      const [document, column, format] = k.split(':');
      return emailLabel({ document, column, format });
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asEmail, selKey]);
  function toggle(kind: RowKind, col: Col, checked: boolean, fmt?: AnnexFormat) {
    setSel((prev) => {
      const next = new Set(prev);
      const key = fmt ? `${kind}:${col}:${fmt}` : `${kind}:${col}`;
      if (checked) next.add(key); else next.delete(key);
      return next;
    });
  }
  function selIaaLangs(): DocumentLanguage[] {
    const l: DocumentLanguage[] = [];
    if ([...sel].some((k) => k.startsWith('iaa:pt'))) l.push('PT');
    if ([...sel].some((k) => k.startsWith('iaa:en'))) l.push('EN');
    return l;
  }
  function selItems(): DocumentsZipItem[] {
    return [...sel].map((k) => {
      const [document, column, format] = k.split(':');
      return { document, column, ...(format ? { format } : {}) } as DocumentsZipItem;
    });
  }
  function closeModal() {
    closeModalRaw();
    setPendingOthers([]); // success cleared sel inside onSubmit; cancel keeps sel
  }

  // "On record" summary for the IAA (display-only; upload waits for SharePoint).
  const docs = (docsQ.data?.items ?? []).filter((d) => !d.deleted_at);
  const original = docs.find((d) => d.kind === 'IAA_INITIAL');
  const amendments = docs.filter((d) => d.kind === 'AMENDMENT').length;
  const closeDoc = docs.find((d) => d.kind === 'CLOSE');
  const iaaOnRecord = original
    ? [
        `Original ${original.signed_date ?? '(undated)'}`,
        amendments ? `${amendments} amendment${amendments === 1 ? '' : 's'}` : null,
        closeDoc ? `closed ${closeDoc.signed_date ?? ''}`.trim() : null,
      ].filter(Boolean).join(' · ')
    : null;

  const rows: { kind: RowKind; label: string; note?: string | null }[] = [
    { kind: 'banktransfer', label: ROW_NAME.banktransfer },
    { kind: 'client', label: ROW_NAME.client },
    { kind: 'amlkyc', label: ROW_NAME.amlkyc },
    { kind: 'iaa', label: ROW_NAME.iaa, note: iaaOnRecord ? `On record: ${iaaOnRecord}` : null },
    { kind: 'fees', label: ROW_NAME.fees },
    { kind: 'aq', label: ROW_NAME.aq },
    { kind: 'pp', label: ROW_NAME.pp },
    { kind: 'samplemonthly', label: ROW_NAME.samplemonthly },
  ];

  // Download the non-IAA selected items sequentially, with a per-item reason on
  // failure (§3j — e.g. "no adequacy assessment yet" instead of a bare code).
  async function downloadOthers(items: { kind: RowKind; col: Col; fmt?: AnnexFormat }[]) {
    if (!items.length) return;
    // Space out consecutive downloads: firing many a.click() back-to-back trips the
    // browser's "trying to download multiple files" gate and silently DROPS some — the
    // 2026-07-16 prod-smoke symptom (up to 14 selected via Download individual: toast
    // fired, files missing). A short gap between files lets each download register
    // before the next. Single-file downloads are unaffected (loop runs once → no gap).
    // For large selections the ZIP path (Download zip) is the one-file alternative.
    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
    // A filled client-record cell becomes one file per picked person; the blank (Fillable)
    // cell is one job that downloads the form(s) the parties need (DF1).
    const jobs = items.flatMap((it) => (it.kind === 'client' && it.col !== 'fillable'
      ? (clientPicksRef.current ?? []).map((person) => ({ ...it, person }))
      : [{ ...it, person: undefined as ClientPick | undefined }]));
    setIndivBusy(true);
    let ok = 0;
    const failed: string[] = [];
    for (let idx = 0; idx < jobs.length; idx += 1) {
      const { kind, col, fmt, person } = jobs[idx];
      if (idx > 0) await sleep(400);
      try {
        if (kind === 'client' && col === 'fillable') {
          const forms = blankClientFormKinds(partiesQ.data?.items ?? []);
          for (let f = 0; f < forms.length; f += 1) {
            if (f > 0) await sleep(400);
            const r = await downloadLegalTemplate(forms[f], agreement.preferred_language, 'pdf');
            downloadBlob(r.blob, r.fileName);
          }
        } else if (kind === 'client' && person) {
          const r = await downloadClientRecord(agreement.id, person.personId, langOf(col));
          downloadBlob(r.blob, r.fileName);
        } else if (kind === 'aq' && col === 'fillable') {
          const r = await downloadAqFillable(agreement.id);
          downloadBlob(r.blob, r.fileName);
        } else if (kind === 'aq') {
          const r = await exportAqDraft(agreement.id, { language: langOf(col) });
          downloadBlob(r.blob, r.fileName);
        } else if (kind === 'fees' || kind === 'pp' || kind === 'samplemonthly') {
          // Annex I honours the per-cell Word/PDF pick; the others serve native
          // (samplemonthly = PDF, pp = Word) and ignore the format arg. A fee simulation,
          // when chosen, goes into the Annex I PDF only.
          const sim = kind === 'fees' && fmt === 'pdf' ? simulationRef.current ?? undefined : undefined;
          const r = await downloadAnnex(agreement.id, kind as AnnexKind, langOf(col), fmt ?? 'word', sim);
          downloadBlob(r.blob, r.fileName);
        }
        ok += 1;
      } catch (e) {
        failed.push(
          e instanceof AqNoAssessmentError
            ? `${itemLabel(kind, col, fmt)} — no adequacy assessment on record yet`
            : person
              ? `${itemLabel(kind, col, fmt)} — ${person.name}${e instanceof Error ? `: ${e.message}` : ''}`
              : itemLabel(kind, col, fmt),
        );
      }
    }
    setIndivBusy(false);
    notifications.show({
      color: failed.length ? 'orange' : 'green',
      icon: failed.length ? <IconAlertCircle size={16} /> : <IconCheck size={16} />,
      title: failed.length ? 'Download — partial' : 'Download complete',
      message: `Downloaded ${ok} document${ok === 1 ? '' : 's'}${failed.length ? `; ${failed.length} failed: ${failed.join('; ')}` : ''}.`,
    });
  }

  // POST the whole selection once as one server ZIP; or, for Email, hand it to the record
  // Email dialog (P34), which generates the same files when the draft is prepared.
  // IAA params optional.
  async function postBundle(kind: 'zip' | 'email', iaa: IaaParams | null) {
    const sim = simulationRef.current;
    const picks = clientPicksRef.current;
    const body: DocumentsZipRequestBody = {
      items: selItems(),
      ...(iaa ? { iaa } : {}),
      ...(picks?.length ? { client_record: { person_ids: picks.map((p) => p.personId) } } : {}),
      ...(sim?.length ? {
        fee_examples: sim.map((e) => ({
          schedule_id: e.scheduleId, amount_eur: e.amountEur, frequency: e.frequency })),
      } : {}),
    };
    if (kind === 'email') {
      // One label per file the draft will carry (a filled client record: one per person).
      const labels = body.items.flatMap((it) => {
        const label = emailLabel(it);
        return it.document === 'client' && it.column !== 'fillable'
          ? (picks ?? []).map((p) => `${label} — ${p.name}`)
          : [label];
      });
      // The people picked in the questions, suggested for To (Pedro's smoke 2026-10-14, step 6).
      const partyItems = partiesQ.data?.items ?? [];
      const people = [
        ...(iaa?.parties ?? []).map((p) => partyItems.find((x) => x.id === p.party_id)?.person_id),
        ...(picks ?? []).map((p) => p.personId),
      ].filter((x): x is string => !!x);
      attachedRef.current = true;
      setAttachedKey(selKey);
      onEmailSelection?.({ body, labels, people: [...new Set(people)] });
      return;
    }
    setZipBusy(true);
    try {
      const r = await downloadDocumentsZip(agreement.id, body);
      downloadBlob(r.blob, r.fileName);
      notifications.show(
        r.skipped > 0
          ? {
              color: 'orange', icon: <IconAlertCircle size={16} />,
              title: 'Bundle ready — partial',
              message: `${r.fileName} · ${r.skipped} skipped: ${r.skippedDetail ?? ''}`,
            }
          : {
              color: 'green', icon: <IconCheck size={16} />,
              title: 'Bundle downloaded', message: r.fileName,
            },
      );
    } finally {
      setZipBusy(false);
    }
  }

  // Download individual — IAA (needs Parties/Date) routes through the modal FIRST;
  // the remaining files download only after it resolves. Selection clears at the end.
  async function handleDownloadIndividual() {
    const iaaLangs = selIaaLangs();
    const others = [...sel]
      .filter((k) => !k.startsWith('iaa:'))
      .map((k) => {
        const [kind, col, fmt] = k.split(':');
        return { kind, col, fmt } as { kind: RowKind; col: Col; fmt?: AnnexFormat };
      });
    if (iaaLangs.length) {
      setModalMode('individual');
      setPendingOthers(others);
      setModalLangs(iaaLangs);
      openModal();
      return;
    }
    await downloadOthers(others);
    setSel(new Set());
  }

  // Download zip / Email — the whole selection in one request. IAA (if ticked) collects
  // its Parties/Date via the modal first (which shows its own errors); otherwise a failure
  // is shown here and the selection stays.
  async function handleBundle(kind: 'zip' | 'email') {
    const iaaLangs = selIaaLangs();
    if (iaaLangs.length) {
      setModalMode(kind);
      setModalLangs(iaaLangs);
      openModal();
      return;
    }
    try {
      await postBundle(kind, null);
      if (kind === 'zip') setSel(new Set());   // Email: cleared once the draft is prepared
    } catch (e) {
      showAlert({
        color: 'red',
        title: kind === 'zip' ? 'Download failed' : 'Email draft not prepared',
        message: e instanceof Error ? e.message : 'Unknown error',
      });
    }
  }

  // Every action starts here: with an Annex I PDF ticked, ask about a fee simulation
  // first (Pedro 2026-10-10), then run the normal flow above.
  const annexPdfSelected = sel.has('fees:pt:pdf') || sel.has('fees:en:pdf');
  const annexWordSelected = sel.has('fees:pt:word') || sel.has('fees:en:word');
  // Only a FILLED client record asks whose records to generate; the blank form needs no pick.
  const clientSelected = [...sel].some((k) => k.startsWith('client:') && !k.startsWith('client:fillable'));
  function runDownload(mode: DownloadMode) {
    // A ticked client record first asks whose records to generate (P16b step c).
    if (clientSelected && clientPicksRef.current === null) {
      setAskClientFor(mode);
      return;
    }
    void (mode === 'individual' ? handleDownloadIndividual() : handleBundle(mode));
  }
  function continueWithClientPicks(picks: ClientPick[]) {
    const mode = askClientFor;
    clientPicksRef.current = picks;
    setAskClientFor(null);
    if (mode) runDownload(mode);
  }
  function startDownload(mode: DownloadMode) {
    simulationRef.current = null;
    clientPicksRef.current = null;
    if (annexPdfSelected && (billingQ.isLoading || scheduleSims.length > 0)) {
      setAskSimulation(mode);
      return;
    }
    runDownload(mode);
  }
  // Email variant: the dialog's Prepare email asks for the generation (one step).
  useEffect(() => {
    if (asEmail && attachSignal > 0) startDownload('email');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachSignal]);
  function continueWithSimulation() {
    const mode = calcFor;
    if (!mode || !simulationReady) return;
    simulationRef.current = scheduleSims.map((s) => ({
      scheduleId: s.scheduleId, amountEur: simAmounts[s.scheduleId] as number,
      frequency: freqOf(s),
    }));
    setCalcFor(null);
    runDownload(mode);
  }

  // Modal onSubmit dispatch (both may throw IaaExportBlockedError → shown in-modal).
  async function individualSubmit(params: IaaParams) {
    const names: string[] = [];
    for (const lang of modalLangs) {
      const r = await exportIaaDraft(agreement.id, {
        language: lang, document_date: params.document_date, parties: params.parties,
      });
      downloadBlob(r.blob, r.fileName);
      names.push(r.fileName);
    }
    notifications.show({
      color: 'green', icon: <IconCheck size={16} />,
      title: `IAA draft${names.length > 1 ? 's' : ''} exported`,
      message: `${names.join(', ')} — marked DRAFT; the signed copy on file remains the authoritative IAA.`,
    });
    if (pendingOthers.length) await downloadOthers(pendingOthers);
    setSel(new Set());
  }
  async function bundleSubmit(params: IaaParams) {
    const kind = modalMode === 'email' ? 'email' : 'zip';
    await postBundle(kind, params);
    if (kind === 'zip') setSel(new Set());
  }

  const cols = asEmail ? COLS.filter((c) => c.key !== 'onfile') : COLS;
  const attached = asEmail && attachedKey !== null && attachedKey === selKey && sel.size > 0;
  const pending = asEmail && sel.size > 0 && !attached;
  useEffect(() => { if (asEmail) onPendingChange?.(pending); }, [asEmail, pending, onPendingChange]);

  return (
    <Stack>
      {!asEmail && <AgreementReportsSection agreementId={agreement.id} />}
      <Card withBorder p={asEmail ? 'sm' : 'lg'} radius="md">
      <Stack gap="sm">
        {!asEmail && (
          <UnstyledButton onClick={toggleLegal}>
            <Group gap="xs" align="center">
              {legalOpen ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
              <IconFileText size={20} />
              <Title order={4}>Legal documents</Title>
            </Group>
          </UnstyledButton>
        )}
        <Collapse in={legalOpen || asEmail}>
        <Stack>
        {asEmail ? (
          <Text size="xs" c="dimmed">
            Tick the documents to attach. They are generated when you press Prepare email; where
            needed it first asks, as Download does, for the IAA parties and date, whose client
            records, and a fee simulation.
          </Text>
        ) : (
        <Text size="sm" c="dimmed">
          The pre-contractual and contractual document set (latest edition only). Tick the
          artefacts you want, then use one of the actions above the table. "On file - signed"
          is the authoritative signed copy: view or download it there, and in Edit mode add
          or replace it (uploaded when you save; every earlier copy stays under "Documents on
          file"). "Fillable" is a blank branded form; "Filled-PT" / "Filled-EN" generate a
          watermarked draft in that language — pick Word or PDF where offered (the on-brand
          PDF is available for Annex I and Annex III today; more documents follow). The
          client record is a PDF filled in from each party's data, without a watermark (it is
          the copy the client checks and signs): downloading it asks whose records to generate. Downloading an Annex I PDF offers to add a fee simulation.
        </Text>
        )}

        {!asEmail && (
        <Group justify="flex-end" gap="sm" align="center">
          <Button
            size="xs"
            leftSection={<IconDownload size={14} />}
            onClick={() => startDownload('individual')}
            loading={indivBusy}
            disabled={sel.size === 0}
          >
            Download individual ({sel.size})
          </Button>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconFileZip size={14} />}
            onClick={() => startDownload('zip')}
            loading={zipBusy}
            disabled={sel.size === 0}
          >
            Download zip
          </Button>
        </Group>
        )}

        <Table withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Document</Table.Th>
              {cols.map((c) => (
                <Table.Th
                  key={c.key}
                  style={{ width: c.key === 'pt' || c.key === 'en' ? 150 : 120, textAlign: 'center' }}
                >
                  {c.label}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => {
              // A document with no actionable column is "not available yet" —
              // dim the whole row so that reads at a glance (§3h), while the
              // per-cell disabled checkboxes keep their reason tooltips.
              const rowUnavailable = !COLS.some((c) => cellState(row.kind, c.key).enabled);
              return (
              <Table.Tr key={row.kind}>
                <Table.Td>
                  <Text size="sm" fw={500} c={rowUnavailable ? 'dimmed' : undefined}>{row.label}</Text>
                  {rowUnavailable && <Text size="xs" c="dimmed" mt={2}>Not available yet</Text>}
                  {row.note && <Text size="xs" c="dimmed" mt={2}>{row.note}</Text>}
                </Table.Td>
                {cols.map((c) => {
                  const st = cellState(row.kind, c.key);
                  // Filled-PT / Filled-EN: a [Word | PDF] pair per cell — each its
                  // own checkbox + availability (Legal-docs PDF arc). A format is
                  // enabled only when the cell is enabled AND that format exists.
                  if (c.key === 'pt' || c.key === 'en') {
                    return (
                      <Table.Td key={c.key} style={{ textAlign: 'center' }}>
                        <Group justify="center" gap="sm" wrap="nowrap">
                          {filledFormats(row.kind).map((f) => {
                            const enabled = st.enabled && f.enabled;
                            const reason = !st.enabled ? st.reason : (!f.enabled ? f.reason : undefined);
                            const key = `${row.kind}:${c.key}:${f.fmt}`;
                            const box = (
                              <Checkbox
                                size="xs"
                                aria-label={`${row.label} — ${c.label} — ${f.fmt.toUpperCase()}`}
                                label={<Text size="xs" c={enabled ? undefined : 'dimmed'}>{f.fmt === 'pdf' ? 'PDF' : 'Word'}</Text>}
                                checked={sel.has(key)}
                                disabled={!enabled}
                                onChange={(e) => toggle(row.kind, c.key, e.currentTarget.checked, f.fmt)}
                              />
                            );
                            return (
                              <span key={f.fmt} style={{ display: 'inline-flex' }}>
                                {!enabled && reason ? (
                                  <Tooltip label={reason} withArrow multiline w={240}>
                                    <span style={{ display: 'inline-flex' }}>{box}</span>
                                  </Tooltip>
                                ) : box}
                              </span>
                            );
                          })}
                        </Group>
                      </Table.Td>
                    );
                  }
                  // P23: On file - signed — the signed copy's status + View / Add.
                  const signedKind = c.key === 'onfile' ? SIGNED_KIND[row.kind] : undefined;
                  if (signedKind && staging) {
                    return (
                      <Table.Td key={c.key} style={{ textAlign: 'center' }}>
                        <OnFileCell docKind={signedKind} files={filesQ.data} staging={staging}
                          editMode={editMode} canWrite={canWriteFiles} onAdd={setAddKind} />
                      </Table.Td>
                    );
                  }
                  // Fillable (and On file for a template without a signed copy): a
                  // single checkbox (no format axis).
                  const key = `${row.kind}:${c.key}`;
                  const box = (
                    <Checkbox
                      size="sm"
                      aria-label={`${row.label} — ${c.label}`}
                      checked={sel.has(key)}
                      disabled={!st.enabled}
                      onChange={(e) => toggle(row.kind, c.key, e.currentTarget.checked)}
                    />
                  );
                  return (
                    <Table.Td key={c.key} style={{ textAlign: 'center' }}>
                      <Group justify="center" gap={0}>
                        {!st.enabled && st.reason ? (
                          <Tooltip label={st.reason} withArrow multiline w={240}>
                            <span style={{ display: 'inline-flex' }}>{box}</span>
                          </Tooltip>
                        ) : box}
                      </Group>
                    </Table.Td>
                  );
                })}
              </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
        </Stack>
        </Collapse>
      </Stack>

      <Modal opened={askSimulation !== null} onClose={() => { setAskSimulation(null); questionClosed(); }} centered
        title={<Text fw={600}>Include a fee simulation?</Text>}>
        <Stack>
          <Text size="sm">Do you want to include a fee simulation in Annex I (PDF)?</Text>
          <Text size="xs" c="dimmed">
            A worked example of this agreement's fee schedule at an amount you choose, added
            under its terms.
            {annexWordSelected && ' The Word Annex I stays without it.'}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => { setAskSimulation(null); questionClosed(); }}>Cancel</Button>
            <Button variant="default" onClick={() => {
              const mode = askSimulation;
              setAskSimulation(null);
              if (mode) runDownload(mode);
            }}>
              No
            </Button>
            <Button onClick={() => {
              const mode = askSimulation;
              setAskSimulation(null);
              setSimAmounts(Object.fromEntries(scheduleSims.map((s) => [s.scheduleId, s.defaultAmount])));
              setSimFreqs(Object.fromEntries(scheduleSims.map((s) => [s.scheduleId, s.defaultFrequency])));
              setCalcFor(mode);
            }}>
              Yes
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Pedro's smoke 2026-10-14: tall and wide enough not to scroll — several schedules sit
          side by side, and the window uses nearly the full height. */}
      <Modal opened={calcFor !== null} onClose={() => { setCalcFor(null); questionClosed(); }}
        size={scheduleSims.length > 1 ? '95%' : 'xl'} yOffset="1vh"
        title={(
          <Group gap="xs">
            <IconCalculator size={18} />
            <Text fw={600}>Fee simulation for Annex I — {agreement.code_1}</Text>
          </Group>
        )}>
        <Stack>
          {!billingQ.isLoading && scheduleSims.length === 0 ? (
            <Alert color="orange" icon={<IconAlertCircle size={16} />}>
              No fee schedule is assigned to this agreement's accounts (Fees → Billing details),
              so there is nothing to simulate.
            </Alert>
          ) : (
            <>
              <Text size="xs" c="dimmed">
                One worked example for each fee schedule on this agreement's accounts, at their
                VAT. Each amount starts from the accounts' latest AuM; where there is none, type
                it (for a Prospect, the expected investment). Each billing frequency starts from
                the one most of the schedule's accounts use; the example shows only the
                frequency chosen.
              </Text>
              <SimpleGrid cols={{ base: 1, lg: Math.min(scheduleSims.length, 2) }} spacing="md">
                {scheduleSims.map((s, i) => (
                  <ScheduleSimulationCard key={s.scheduleId} sim={s}
                    amount={simAmounts[s.scheduleId] ?? null}
                    onAmount={(v) => setSimAmounts((prev) => ({ ...prev, [s.scheduleId]: v }))}
                    frequency={freqOf(s)}
                    onFrequency={(f) => setSimFreqs((prev) => ({ ...prev, [s.scheduleId]: f }))}
                    example={exampleQs[i]?.data}
                    error={(exampleQs[i]?.error as ApiError | null | undefined)?.detail ?? null} />
                ))}
              </SimpleGrid>
            </>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => { setCalcFor(null); questionClosed(); }}>Cancel</Button>
            <Button leftSection={<IconDownload size={16} />} disabled={!simulationReady}
              onClick={continueWithSimulation}>
              Continue
            </Button>
          </Group>
        </Stack>
      </Modal>

      {askClientFor !== null && (
        <ClientRecordPartiesModal
          agreement={agreement}
          parties={partiesQ.data?.items ?? []}
          opened
          onClose={() => { setAskClientFor(null); questionClosed(); }}
          onContinue={continueWithClientPicks}
        />
      )}

      {modalOpened && modalLangs.length > 0 && (
        <ExportIaaModal
          agreement={agreement}
          holders={holders}
          langs={modalLangs}
          opened={modalOpened}
          onClose={() => { closeModal(); questionClosed(); }}
          onSubmit={modalMode === 'individual' ? individualSubmit : bundleSubmit}
          submitLabel={modalMode === 'zip' ? 'Download zip'
            : modalMode === 'email' ? 'Continue to Email'
              : `Export IAA draft${modalLangs.length > 1 ? 's' : ''}`}
        />
      )}
      </Card>

      {/* P23 (2026-10-10): every file on this agreement — the signed copies above with
          their earlier versions, IAA amendments and other documents. */}
      {!asEmail && staging && (
        <>
          <Card withBorder p="lg" radius="md">
            <Stack gap="sm">
              <Group gap="xs" align="center">
                <IconFiles size={20} />
                <Title order={4}>Documents on file</Title>
              </Group>
              <DocumentsPanel ownerType="agreements" ownerId={agreement.id}
                editMode={editMode} staging={staging} />
            </Stack>
          </Card>

          <AddDocumentModal
            opened={addKind !== null}
            onClose={() => setAddKind(null)}
            kinds={kindsFor(kindsQ.data, 'agreements')}
            presetKind={addKind}
            onAdd={staging.stageUpload}
          />
        </>
      )}
    </Stack>
  );
}
